/**
 * Dyno curve synthesis from stored peak figures.
 *
 * Pure, deterministic, dependency-free: given a vehicle's stock/tuned peak
 * BHP and Nm plus engine context (fuel, aspiration, vehicle class), build
 * smooth dyno-style curves across the RPM range. Shape follows engine
 * character (turbo torque plateau, NA top-end build, heavy-plant low revs);
 * peaks always land exactly on the stored figures.
 *
 * Physics anchor: bhp = Nm × rpm / 7121.6. The torque shape is solved so the
 * derived power peak equals the stored BHP wherever the figures are mutually
 * consistent; a bounded correction ramp is applied only when they are not.
 */

const NM_RPM_PER_BHP = 7121.6;

const TURBO_TOKENS =
  /(tdi|tsi|tfsi|t-jet|tce|jtd|cdti|tdci|crdi|hdi|dci|d-4d|bluehdi|ecoboost|turbo|biturbo|multijet|multi[- ]air|mjt|vgt|gti|kompressor|supercharg|s\/c)/i;
const SC_TOKENS = /(kompressor|supercharg|s\/c)/i;

export function classifyEngine({ fuel, engine, engineSize, originalNm, vehicleType }) {
  const heavy = vehicleType === "Trucks / HGV" || vehicleType === "Agricultural";
  const diesel = /diesel/i.test(fuel || "") || heavy;
  const e = (engine || "").toLowerCase();
  let aspiration = "na";
  if (SC_TOKENS.test(e)) aspiration = "supercharged";
  else if (TURBO_TOKENS.test(e)) aspiration = "turbo";
  else {
    const m = String(engineSize || e).match(/(\d\.\d)/);
    const litres = m ? parseFloat(m[1]) : 0;
    if (diesel) aspiration = "turbo"; // modern diesel plant is effectively all turbocharged
    else if (litres > 0 && originalNm > 0 && originalNm / litres >= 118) aspiration = "turbo"; // Nm/L beyond NA reach
  }
  return { diesel, aspiration, heavy };
}

/** Operating window and torque-peak placement per engine character. */
export function rpmProfile(cls) {
  if (cls.heavy) return { start: 700, redline: 2400, tqPeak: 1400, plateauEnd: 1600, baseFrac: 0.62, endFrac: 0.55, rolloffSpan: 0.35 };
  if (cls.diesel) return { start: 900, redline: 4700, tqPeak: 1900, plateauEnd: 2900, baseFrac: 0.52, endFrac: 0.5, rolloffSpan: 0.45 };
  if (cls.aspiration === "turbo") return { start: 950, redline: 6400, tqPeak: 1750, plateauEnd: 4400, baseFrac: 0.55, endFrac: 0.6, rolloffSpan: 0.45 };
  if (cls.aspiration === "supercharged") return { start: 950, redline: 6700, tqPeak: 3400, plateauEnd: 4200, baseFrac: 0.66, endFrac: 0.68, rolloffSpan: 0.5 };
  return { start: 950, redline: 7000, tqPeak: 4300, plateauEnd: 4550, baseFrac: 0.7, endFrac: 0.76, rolloffSpan: 0.55 }; // naturally aspirated petrol
}

const smoothstep = (t) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));

/**
 * Torque shape on normalized x∈[0,1], peak normalized to 1.0.
 * Knob: plateau end (xp). Rise to the torque peak, hold the plateau, then
 * ease off in RPM space over the class's rolloff window — so when the solver
 * parks the plateau right at the limiter (e.g. high-strung NA engines), the
 * curve ends cleanly at peak instead of cliff-diving at the edge.
 */
function torqueShape(x, p) {
  const { xt, xp, baseFrac, endFrac, start, span, rolloffRpm } = p;
  if (x <= 0) return baseFrac;
  if (x < xt) {
    const t = x / xt;
    return baseFrac + (1 - baseFrac) * Math.pow(smoothstep(t), 1.15);
  }
  if (x <= xp) return 1;
  const rpmPast = x * span - xp * span;
  const t = Math.min(1, rpmPast / rolloffRpm);
  return 1 - (1 - endFrac) * smoothstep(t);
}

function buildSingle(bhp, nm, profile) {
  const { start, redline, tqPeak, plateauEnd, baseFrac, endFrac, rolloffSpan } = profile;
  const span = redline - start;
  const xOf = (rpm) => (rpm - start) / span;
  const xt = xOf(tqPeak);
  const rolloffRpm = rolloffSpan * span;
  const shapeParams = (xp) => ({ xt, xp, baseFrac, endFrac, start, span, rolloffRpm });
  // Required normalized (shape × rpm) maximum for the power peak to equal bhp
  // while torque peaks at nm: max(shape·rpm) = bhp × 7121.6 / nm.
  const target = (bhp * NM_RPM_PER_BHP) / nm;

  // Solve plateau end by bisection: longer plateau → later/higher power peak.
  let lo = xt + 1e-4;
  let hi = 1;
  const peakAt = (xp) => {
    let best = 0;
    const p = shapeParams(xp);
    for (let i = 0; i <= 160; i++) {
      const x = i / 160;
      const v = torqueShape(x, p) * (start + x * span);
      if (v > best) best = v;
    }
    return best;
  };
  const pLo = peakAt(lo);
  const pHi = peakAt(hi);
  let xp = xOf(plateauEnd);
  let correction = 1;
  if (target <= pLo) {
    xp = lo; // figures imply power peaks at/before torque peak — keep pointy plateau
  } else if (target >= pHi) {
    xp = hi; // figures exceed what the rev range can physically carry — keep exact
    // torque curve, correct only the power readout ramp toward the top end
    correction = target / pHi;
  } else {
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      if (peakAt(mid) < target) lo = mid;
      else hi = mid;
    }
    xp = (lo + hi) / 2;
  }

  const p = shapeParams(xp);
  const points = [];
  const n = 61;
  for (let i = 0; i < n; i++) {
    const x = i / (n - 1);
    const rpm = Math.round(start + x * span);
    const tq = torqueShape(x, p) * nm;
    let hp = (tq * rpm) / NM_RPM_PER_BHP;
    if (correction !== 1) {
      // bounded ramp (0 below plateau end → full correction at the power peak
      // zone) so the displayed power peak matches the stored figure exactly
      const ramp = smoothstep((x - xp) / Math.max(1e-6, 1 - xp));
      hp *= 1 + (correction - 1) * ramp;
    }
    points.push({ rpm, tq, hp });
  }
  // Exact peak fidelity: grid sampling can shave fractions off the solved
  // peak, so normalise the power curve onto the stored figure (≈1.00x — the
  // solver already landed it, this removes only quantization error).
  const rawPeak = Math.max(...points.map((pt) => pt.hp));
  const exact = bhp / rawPeak;
  for (const pt of points) {
    pt.hp = Math.round(pt.hp * exact * 10) / 10;
    pt.tq = Math.round(pt.tq * 10) / 10;
  }
  const hpPeak = points.reduce((a, b) => (b.hp > a.hp ? b : a));
  const tqPeakPt = points.reduce((a, b) => (b.tq > a.tq ? b : a));
  return { points, hpPeak, tqPeak: tqPeakPt, redline, start, corrected: correction !== 1 };
}

/**
 * Build all dyno series for a vehicle payload.
 * stages: [{key:'stock'|'stage1'|'stage2'|'stage3', label, bhp, nm}] — caller
 * filters to stages that actually have data. Returns null when the minimum
 * (stock power + torque) is unavailable.
 */
export function buildDynoSeries(vehicle) {
  const { original_bhp: ob, original_nm: on, fuel, engine, engine_size, vehicle_type } = vehicle || {};
  if (!ob || !on || ob <= 0 || on <= 0) return null;
  const cls = classifyEngine({ fuel, engine, engineSize: engine_size, originalNm: on, vehicleType: vehicle_type });
  const profile = rpmProfile(cls);
  const raw = [{ key: "stock", label: "Stock", bhp: ob, nm: on }];
  for (const s of vehicle.display_stages || []) {
    if (!["stage1", "stage2", "stage3"].includes(s.key)) continue; // eco/e85 are not dyno stages here
    if (s.bhp > 0 && s.nm > 0) raw.push({ key: s.key, label: s.label, bhp: s.bhp, nm: s.nm });
  }
  if (raw.length < 2) return null; // nothing tuned to compare against
  const series = raw.map((s) => ({ ...s, ...buildSingle(s.bhp, s.nm, profile) }));
  return {
    series,
    classInfo: cls,
    start: profile.start,
    redline: profile.redline,
    maxBhp: Math.max(...series.map((s) => s.bhp)),
    maxNm: Math.max(...series.map((s) => s.nm)),
  };
}
